import { Flame, MousePointerClick, Link2, Gift, Twitter, CheckCircle2 } from 'lucide-react';

import { useHolidayStore } from '@/store/useHolidayStore';

interface Step {
  id: number;
  label: string;
  icon: React.ReactNode;
  description: string;
}

const STEPS: Step[] = [
  {
    id: 1,
    label: 'Light the Fire',
    icon: <Flame className="w-4 h-4" />,
    description: 'Click on the fireplace to start the ritual',
  },
  {
    id: 2,
    label: 'Click a Present',
    icon: <MousePointerClick className="w-4 h-4" />,
    description: 'Click on any present to open it',
  },
  {
    id: 3,
    label: 'Verify Wrapped Tweet',
    icon: <Link2 className="w-4 h-4" />,
    description: 'Paste your Junk.Fun Wrapped tweet link to verify',
  },
  {
    id: 4,
    label: 'Burn the Past',
    icon: <Gift className="w-4 h-4" />,
    description: 'Drag your selected present into the fire',
  },
  {
    id: 5,
    label: 'Share the Phrase',
    icon: <Twitter className="w-4 h-4" />,
    description: 'Reply to our tweet with the secret phrase & tag 2 friends',
  },
  {
    id: 6,
    label: 'Verify & Complete',
    icon: <CheckCircle2 className="w-4 h-4" />,
    description: 'Paste your reply link to claim your reward',
  },
];

export function StepInstructions() {
  const {
    fireplaceOn,
    selectedGiftId,
    wrappedVerified,
    burnedCount,
    showPhraseReveal,
    tweetVerified,
  } = useHolidayStore();

  // Determine current step
  const getCurrentStep = (): number => {
    if (tweetVerified) return 7; // All done
    if (showPhraseReveal) return 5; // On phrase reveal step, ready to share
    if (wrappedVerified && burnedCount() > 0) return 5; // Gift burned, ready for phrase
    if (wrappedVerified) return 4; // Wrapped verified, needs to burn the gift
    if (selectedGiftId) return 3; // Gift selected, verifying wrapped tweet
    if (fireplaceOn) return 2; // Fire lit, needs to click a present
    return 1; // Need to light fire
  };

  const currentStep = getCurrentStep();

  // Don't show step instructions if user has completed everything
  // The RitualCompleteModal handles the completion state
  if (tweetVerified) {
    return null;
  }

  return (
    <div
      className="absolute z-40 top-4 left-4 bg-black/80 backdrop-blur-sm rounded-xl p-3 border border-orange-500/30 shadow-lg"
      style={{ maxWidth: '300px' }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Flame className="w-5 h-5 text-orange-400" />
        <h3
          className="text-sm font-bold text-orange-400"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          New Year Ritual
        </h3>
      </div>

      {/* Steps */}
      <div className="space-y-2">
        {STEPS.map((step) => {
          const isCompleted = step.id < currentStep;
          const isCurrent = step.id === currentStep;
          const isPending = step.id > currentStep;

          return (
            <div
              key={step.id}
              className={`flex items-start gap-2 p-2 rounded-lg transition-all ${
                isCurrent
                  ? 'bg-orange-500/20 border border-orange-500/40'
                  : isCompleted
                  ? 'opacity-60'
                  : 'opacity-40'
              }`}
            >
              {/* Step indicator */}
              <div
                className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                  isCompleted
                    ? 'bg-green-500 text-white'
                    : isCurrent
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-600 text-gray-400'
                }`}
              >
                {isCompleted ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  <span className="text-xs font-bold">{step.id}</span>
                )}
              </div>

              {/* Step content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span
                    className={`text-xs font-semibold ${
                      isCurrent ? 'text-orange-300' : isCompleted ? 'text-green-400' : 'text-gray-400'
                    }`}
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {step.label}
                  </span>
                  <span className={isCurrent ? 'text-orange-400' : 'text-gray-500'}>
                    {step.icon}
                  </span>
                </div>
                {isCurrent && (
                  <p
                    className="text-[10px] text-gray-300 mt-0.5 leading-tight"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {step.description}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Progress bar */}
      <div className="mt-3 h-1.5 bg-gray-700 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-orange-500 to-yellow-400 transition-all duration-500"
          style={{ width: `${((currentStep - 1) / STEPS.length) * 100}%` }}
        />
      </div>
      <p
        className="text-[10px] text-gray-500 text-center mt-1"
        style={{ fontFamily: 'Outfit, sans-serif' }}
      >
        Step {currentStep} of {STEPS.length}
      </p>
    </div>
  );
}
